/**
 * handoffScrub.ts — take EVERY hand-off id off the address bar, in ONE rewrite.
 *
 * TWO IDS, ONE ADDRESS BAR. `?resume=`/`#resume=` (an assessment id, emailed to
 * a named patient) and `?lead=`/`#lead=` (a lead id, which dereferences to that
 * patient's height, weight and email) can both be on the same URL — the widget
 * mints `/weightloss?assessment=…#lead=…`, and a plan link can arrive alongside
 * a cached embed's `?lead=`. Neither may travel any further than the request
 * that carried it: onward they would ride GTM's `page_location` into a container
 * administered outside this estate, the `Referer` on the next hop, the browser's
 * own history, and every in-app navigation for the rest of the journey.
 *
 * WHY THIS MODULE EXISTS AT ALL, rather than two scrub functions called in a
 * row. That is what the code did, and it was wrong in a way that survived
 * review: a `navigate` inside an effect does NOT refresh `useLocation()` before
 * the next statement runs, so both calls are computed from the same stale
 * snapshot and the second `replace` writes back the key the first has just
 * removed. `/weightloss?resume=…&lead=…` ended up as `/weightloss?resume=…`.
 * Composing the strippers by hand at the call site fixed the end state but left
 * the shape intact — two navigations, ordering held together by a comment — and
 * a reviewer duly split them apart again with every test still green.
 *
 * So the ordering is not a convention any more. There is one function, it owns
 * both keys, it computes the final search/hash BEFORE it navigates, and it
 * navigates ONCE. There is no second call to get the order wrong, and no
 * intermediate URL for anything to read.
 *
 * WHY `navigate({ replace: true })` AND NOT `history.replaceState`. A raw
 * replaceState would leave `useLocation()` stale, so React Router would keep
 * handing the old search/hash to the unsaved-changes guard, which re-emits it on
 * the first keystroke — the id would reappear in the address bar. `replace`
 * leaves no history entry either way, so Back still exits cleanly.
 *
 * WHAT IT CANNOT DO, stated so nobody over-claims it: the request that CARRIED a
 * deprecated `?resume=`/`?lead=` here has already been logged by the load
 * balancer by the time any of this runs. The scrub stops the id going any
 * further. See the two module headers for the rest of that argument.
 *
 * CALLERS MUST CAPTURE THE ID FIRST. `JourneyPage` and `CallbackPage` both
 * freeze what they need in lazy state at mount; after this runs there is nothing
 * left to read.
 */
import type { Location, NavigateFunction } from 'react-router-dom';
import { hasCheckoutReturnKeys, stripCheckoutReturnKeys } from '../checkout/setupRedirectReturn';
import { readLeadId, stripLeadKeys } from './leadHandoff';
import { readResumeId, stripResumeKeys } from './resumeHandoff';

/**
 * Drop `resume=` and `lead=` from both the query and the fragment, in place,
 * with a single `replace` navigation. A no-op when neither id is present, so it
 * is safe to run from an effect on every location change.
 *
 * AND A 3D SECURE RETURN'S KEYS (2026-09-25): Stripe's `setup_intent`,
 * `setup_intent_client_secret` and `redirect_status`, and the portal's own
 * `#checkout-return=` (see `checkout/setupRedirectReturn.ts`). The client secret
 * must not ride `page_location` or sit in history, and a reload must not replay
 * the return — the attach and its funnel events come from the one document the
 * bank sent the patient to. Same one rewrite, for the same one-address-bar
 * reason as the two hand-off ids.
 *
 * Everything else on the URL survives byte for byte — `?assessment=` (the
 * journey's durable identity), the marketing tags Salman's attribution is built
 * on, and a plain `#faq` anchor.
 */
export function scrubHandoffIdsFromUrl(
  navigate: NavigateFunction,
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
): void {
  if (!readResumeId(location) && !readLeadId(location) && !hasCheckoutReturnKeys(location)) return;
  // Strip in sequence on the VALUE, not on the address bar: each stripper is
  // handed the previous one's search/hash, so none can restore another's key.
  // One `navigate` with the finished result.
  const withoutResume = { ...location, ...stripResumeKeys(location) };
  const withoutLead = { ...withoutResume, ...stripLeadKeys(withoutResume) };
  const { search, hash } = stripCheckoutReturnKeys(withoutLead);
  navigate({ pathname: location.pathname, search, hash }, { replace: true });
}
